import { Chip } from "@mui/material";
import moment from "moment";
import { DIRECT_DISPLAY_STATUSES } from "../cf-v2-forms-status-tabs/v2-config";
import { getCfStatusChipSx, getCfStatusLabel } from "../../utils/constants";

// Synthetic pseudo-step for the record's creation date — not a real approval step
// (never seeded into CfStepMaster), always shown ahead of the real Step 1 ("CF Upload").
const CREATION_STEP = { stepNumber: 1, name: "Lead Creation", isCreationEvent: true };

// Fallback step labels used when the live step master list isn't available.
// Kept in sync with the full 22-step workflow seeded in CfStepMaster (cf-v2.constants.js).
const FALLBACK_STEPS = [
  { stepNumber: 1, name: "CF Upload" },
  { stepNumber: 2, name: "Projects Approval" },
  { stepNumber: 3, name: "BD Regional Head" },
  { stepNumber: 4, name: "Regional Head" },
  { stepNumber: 5, name: "Business Finance 1" },
  { stepNumber: 6, name: "BD head" },
  { stepNumber: 7, name: "Operations Head" },
  { stepNumber: 8, name: "Business Finance 2" },
  { stepNumber: 9, name: "COO" },
  { stepNumber: 10, name: "CFO" },
  { stepNumber: 11, name: "CEO" },
  { stepNumber: 12, name: "LOI Upload" },
  { stepNumber: 13, name: "LOI Approval - Legal" },
  { stepNumber: 14, name: "LOI Approval - Business Finance" },
  { stepNumber: 15, name: "LOI Execution" },
  { stepNumber: 16, name: "Possession Request" },
  { stepNumber: 17, name: "Possession Approval - Legal Head" },
  { stepNumber: 18, name: "Possession Letter" },
  { stepNumber: 19, name: "Opening Request – Licenses" },
  { stepNumber: 20, name: "Opening Approval - Legal Head" },
  { stepNumber: 21, name: "Opening Request - Operations" },
  { stepNumber: 22, name: "Opening Approval - Operations" },
];

// Drive the per-step columns from step master so new steps need no code change.
// The creation pseudo-step is always prepended — it isn't a real seeded CfStepMaster step.
// Shared by the on-screen table and the excel export so both always show the same steps.
export const getOrderedV2Steps = (stepMasterList) => [
  CREATION_STEP,
  ...(Array.isArray(stepMasterList) && stepMasterList.length ? stepMasterList : FALLBACK_STEPS),
].sort((a, b) => a.stepNumber - b.stepNumber);

export const getV2HeaderConfig = (userMap, stepMasterList) => {
  const getLastStepAction = (data, stepNumber) => {
    const currentStep = [...(data?.stepHistory || [])].reverse().find((h) => h.stepNumber === stepNumber && ['CREATED', 'APPROVED'].includes(h.action)) || {};
    return currentStep.timestamp ? moment(currentStep.timestamp).format("YYYY-MM-DD HH:mm:ss A") : "N/A";
  };

  // "Lead Creation" always reflects the record's own creation date, regardless of
  // whether/when it later gets approved off Step 1 — distinct from "CF Upload" below.
  const getCreationTimestamp = (data) => {
    const created = (data?.stepHistory || []).find((h) => h.action === "CREATED");
    const ts = created?.timestamp || data?.createdAt;
    return ts ? moment(ts).format("YYYY-MM-DD HH:mm:ss A") : "N/A";
  };

  const steps = getOrderedV2Steps(stepMasterList);

  const stepColumns = steps.map((s) => ({
    label: `Step ${s.stepNumber} (${s.name})`,
    id: "currentStep",
    render: (data) => (
      <span className="cf-v2-dashboard-table-cell">
        {s.isCreationEvent ? getCreationTimestamp(data) : getLastStepAction(data, s.stepNumber)}
      </span>
    ),
  }));

  let headerConfig = [
     {
      label: "CF Id",
      id: "id",      
    },
    {
      label: "Site Name",
      id: "siteName",
      render: (data) => <span className="cf-v2-dashboard-table-cell">{data?.siteName || "N/A"}</span>,
    },
    {
      label: "Region",
      id: "address.region",
      render: (data) => <span>{data?.address?.region || "N/A"}</span>,
    },
    {
      label: "Circle",
      id: "address.circle",
      render: (data) => <span>{data?.address?.circle || "N/A"}</span>,
    },
    {
      label: "City",
      id: "address.city",
      render: (data) => <span>{data?.address?.city || "N/A"}</span>,
    },
    {
      label: "Current Step",
      id: "currentStep",
      render: (data) => {
        const currentStep = data.currentStepDetails;
        return <span className="cf-v2-dashboard-table-cell">{currentStep ? `Step ${currentStep.stepNumber} - ${currentStep.name}` : "N/A"}</span>;
      }
    },
    {
      label: "Status",
      id: "status",
      render: (data) => {        
        if (DIRECT_DISPLAY_STATUSES.includes(data?.status)) {
          return <Chip label={getCfStatusLabel(data.status)} size="small" variant="outlined" sx={getCfStatusChipSx(data.status)} />;
        }
        const currentStep = data.currentStepDetails;
        return <span>{currentStep ? <Chip label={getCfStatusLabel(currentStep.cfStatus)} size="small" variant="outlined" sx={getCfStatusChipSx(currentStep.cfStatus)} /> : "N/A"}</span>;
      }
    },
    {
      label: "Store Code",
      id: "storeCode",
      render: (data) => {
        if (!data.storeCode) {
          return "-"
        }
        return <span className="cf-v2-table-store-code">{`${data.storeCode}-${data.sequenceCode || 'X'}-${data.satelliteCode || 'X'}`}</span>
      }
    },
    {
      label: "Created By",
      id: "createdBy",
      render: (data) => <span className="cf-v2-dashboard-table-cell">{userMap[data?.createdBy] || "N/A"}</span>,
    },
    {
      label: "Relocated",
      id: "relocated",
      render: (data) =>
        <span className="cf-v2-dashboard-table-cell">{data?.relocated ? "Yes" : "No"}</span>,
    },
    {
      label: "Part of AOP list",
      id: "partOfAOP",
      render: (data) =>
        <span className="cf-v2-dashboard-table-cell">{data?.partOfAOP ? "Yes" : "No"}</span>,
    },
    ...stepColumns,
  ];


  return headerConfig;
};
